import React, { useState } from 'react';
import { SendIcon, CheckCircleIcon, XCircleIcon, RefreshCwIcon } from 'lucide-react';
import type { MessageState } from '../../stores/bulkSendStore';

interface BulkSendControlsProps {
  messages: MessageState[];
  sending: boolean;
  progress: {
    total: number;
    sent: number;
    failed: number;
    current: number;
  };
  onSend: () => Promise<void>;
  onRetryFailed: () => void;
}

export function BulkSendControls({
  messages,
  sending,
  progress,
  onSend,
  onRetryFailed,
}: BulkSendControlsProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const pendingMessages = messages.filter((m) => m.status === 'pending');
  const sentMessages = messages.filter((m) => m.status === 'sent');
  const failedMessages = messages.filter((m) => m.status === 'failed');

  const hasCompleted = sending === false && (sentMessages.length > 0 || failedMessages.length > 0);
  const hasFailed = failedMessages.length > 0;

  const estimatedTimeSeconds = pendingMessages.length * 2; // 2 seconds per message
  const estimatedMinutes = Math.floor(estimatedTimeSeconds / 60);
  const estimatedRemainingSeconds = estimatedTimeSeconds % 60;

  const handleSendClick = () => {
    if (pendingMessages.length > 5) {
      // Show confirmation for bulk sends
      setShowConfirm(true);
    } else {
      onSend();
    }
  };

  const handleConfirmSend = () => {
    setShowConfirm(false);
    onSend();
  };

  // If sending, show progress
  if (sending) {
    const percentage = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
    const remainingMessages = progress.total - progress.current;
    const remainingSeconds = remainingMessages * 2;
    const remainingMinutes = Math.floor(remainingSeconds / 60);
    const remainingSecondsDisplay = remainingSeconds % 60;

    return (
      <div className="bg-white border-t border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-700 font-medium">
            Sending: {progress.current}/{progress.total} messages
          </span>
          <span className="text-gray-500">{percentage}%</span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-[#5B8E87] transition-all duration-300 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <CheckCircleIcon className="w-4 h-4 text-green-600" />
              {progress.sent} sent
            </span>
            {progress.failed > 0 && (
              <span className="flex items-center gap-1">
                <XCircleIcon className="w-4 h-4 text-red-600" />
                {progress.failed} failed
              </span>
            )}
          </div>
          {remainingMessages > 0 && (
            <span>
              Est. {remainingMinutes > 0 ? `${remainingMinutes}m ` : ''}
              {remainingSecondsDisplay}s remaining
            </span>
          )}
        </div>

        <p className="text-xs text-center text-gray-500">
          ⏱️ Sending 1 message every 2 seconds to avoid spam detection
        </p>
      </div>
    );
  }

  // If completed, show results summary
  if (hasCompleted) {
    return (
      <div className="bg-white border-t border-gray-200 p-4 space-y-3">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {hasFailed ? 'Sending Completed with Errors' : 'Successfully Sent!'}
          </h3>
          <div className="flex items-center justify-center gap-6 mb-3">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">{sentMessages.length}</span> sent
              </span>
            </div>
            {hasFailed && (
              <div className="flex items-center gap-2">
                <XCircleIcon className="w-5 h-5 text-red-600" />
                <span className="text-sm text-gray-700">
                  <span className="font-semibold text-red-700">{failedMessages.length}</span> failed
                </span>
              </div>
            )}
          </div>
        </div>

        {hasFailed && (
          <button
            onClick={onRetryFailed}
            className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCwIcon className="w-5 h-5" />
            Retry {failedMessages.length} Failed {failedMessages.length === 1 ? 'Message' : 'Messages'}
          </button>
        )}
      </div>
    );
  }

  // Default state - show send button
  return (
    <>
      <div className="bg-white border-t border-gray-200 p-4 space-y-3">
        <button
          onClick={handleSendClick}
          disabled={pendingMessages.length === 0}
          className="w-full bg-[#5B8E87] text-white py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SendIcon className="w-5 h-5" />
          Send to {pendingMessages.length} {pendingMessages.length === 1 ? 'Client' : 'Clients'}
        </button>

        {pendingMessages.length > 0 && (
          <p className="text-xs text-center text-gray-500">
            ⏱️ Estimated time:{' '}
            {estimatedMinutes > 0 && `${estimatedMinutes}m `}
            {estimatedRemainingSeconds}s (1 message every 2 seconds)
          </p>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Bulk Send</h3>
            <p className="text-sm text-gray-600 mb-4">
              You're about to send {pendingMessages.length} messages. This will take approximately{' '}
              {estimatedMinutes > 0 ? `${estimatedMinutes} minute${estimatedMinutes > 1 ? 's' : ''} and ` : ''}
              {estimatedRemainingSeconds} seconds.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Messages will be sent with a 2-second delay between each to avoid spam detection.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSend}
                className="flex-1 px-4 py-2 bg-[#5B8E87] text-white rounded-xl hover:bg-[#4F7A74] transition-colors"
              >
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
